import { type TermsOfUseStore } from '@/features/account/terms/TermsOfUseStore'
import { Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, observable } from 'mobx'
import { TermsOfUseDocument } from '@/features/account/terms/TermsOfUseDocument'
import { task } from 'mobx-task'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ignoreAbortError, isAbortError } from '@/utils/abort-util'
import type { EnsureTermsAcceptanceResult } from '@/features/account'
import type { GetTermsOfUseConfigFn } from '@/features/account/internal'

/**
 * View model for the TOU acceptance dialog.
 */
export class TermsOfUseAcceptanceDialogViewModel {
  /**
   * The workspace id.
   */
  private _workspaceId = Id.none

  /**
   * The modal state.
   */
  readonly modal = new ModalViewModel()

  /**
   * Manages the prompt state.
   */
  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  /**
   * The current terms.
   */
  @observable accessor terms: TermsOfUseDocument = TermsOfUseDocument.none

  /**
   * Whether the user has checked the agree box.
   */
  @observable accessor agreed = false

  /**
   * Whether the user has scrolled to the bottom of the terms.
   */
  @observable accessor isScrolledToBottom = false

  /**
   * Constructor.
   */
  constructor(
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _termsOfUseStore: TermsOfUseStore,
    private readonly _getTermsConfig: GetTermsOfUseConfigFn,
  ) {}

  /**
   * Prompts for acceptance.
   */
  async prompt(
    workspaceId: Id,
    signal?: AbortSignal,
  ): Promise<EnsureTermsAcceptanceResult> {
    if (!this._getTermsConfig(workspaceId).enabled) {
      // If TOS is not enabled, that's just an implicit acceptance.
      return 'Accepted'
    }

    const accepted =
      await this._termsOfUseStore.checkTermsOfUseAcceptance(workspaceId)

    if (accepted) {
      return 'Accepted'
    }

    if (signal?.aborted) {
      return 'Aborted'
    }

    this.setAgreed(false)
    this.setScrolledToBottom(false)
    const promise = this._prompt.prompt(signal)
    this.modal.show()
    try {
      const fetchResult = await this.fetchLatestTerms(workspaceId, signal)
        .catch(ignoreAbortError())
        .catch(this._createSnackbarErrorHandler('Fetching latest terms failed'))
      if (isAbortError(fetchResult)) {
        return 'Aborted'
      }

      const response = await promise
      if (signal?.aborted) {
        return 'Aborted'
      }

      return response ? 'Accepted' : 'Declined'
    } finally {
      this.modal.close()
    }
  }

  /**
   * Fetches the latest terms.
   */
  fetchLatestTerms = task.resolved(
    async (
      workspaceId: string | undefined,
      signal: AbortSignal | undefined,
    ) => {
      const latestTerms =
        await this._termsOfUseStore.fetchLatestTermsOfUseDocument(
          workspaceId,
          signal,
        )
      this.setTermsDocument(workspaceId, latestTerms)
    },
  )

  /**
   * Sets the current terms document.
   *
   * @param workspaceId
   * @param terms
   */
  @action.bound
  setTermsDocument(workspaceId: Id, terms: TermsOfUseDocument) {
    this._workspaceId = workspaceId
    this.terms = terms
  }

  /**
   * Sets the agreed state.
   *
   * @param agreed
   */
  @action.bound
  setAgreed(agreed: boolean) {
    this.agreed = agreed
  }

  /**
   * Sets whether the user has scrolled to the bottom.
   *
   * @param scrolled
   */
  @action.bound
  setScrolledToBottom(scrolled: boolean) {
    this.isScrolledToBottom = scrolled
  }

  /**
   * Accepts the terms.
   */
  accept = task.resolved(async () => {
    await this._termsOfUseStore.acceptLatestTerms(this._workspaceId)
    this._prompt.respond(true)
  })

  /**
   * Declines the terms.
   */
  @action.bound
  decline() {
    this._prompt.respond(false)
  }
}
